import { fg } from '@atlassian/jira-feature-gating';
import type { ChildRowsPreloadedQuery$variables } from '@atlassian/jira-relay/src/__generated__/ChildRowsPreloadedQuery.graphql';
import { COLUMN_ID_MERGED_COLUMN } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { getOperationScope } from '@atlassian/jira-issue-table-common/src/utils/index.tsx';

export interface Column {
	id: string;
	mergedColumns?: Column[];
	stickyOffsets?: number;
	isColumnSticky?: boolean;
}

interface BaseProps<TColumn extends Column> {
	cloudId: string;
	columns: TColumn[];
	isReparentingEnabled: boolean;
	isTimelineEnabled: boolean;
	actionColumns: string[];
	amountOfColumns: number;
	pageSize: number;
	projectKey?: string;
	isInlineFieldConfigEnabled: boolean;
	isInlineEditingEnabled: boolean;
}

interface ChildQueryForJqlProps<TColumn extends Column> extends BaseProps<TColumn> {
	jql: string;
	isHideDoneItemsEnabled: boolean;
	isConditionalFormattingEnabled: boolean;
	isLinkRelationshipFieldEnabled: boolean;
	isRemoteLinkedIssuesEnabled: boolean;
	isEditableSyncLoaded: boolean;
}

const getProjectKeyFromIssueKey = (issueKey: string): string => {
	return issueKey.split('-')[0];
};

const getChildQueryVariablesBase = <TColumn extends Column>({
	cloudId,
	columns,
	isReparentingEnabled,
	isTimelineEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
	projectKey,
	isInlineFieldConfigEnabled,
	isInlineEditingEnabled,
}: BaseProps<TColumn>) => {
	let fieldSetIds = columns
		.filter((column: TColumn) => !!column.id && !actionColumns.includes(column.id))
		.map((column: TColumn) => column.id)
		.sort();

	const mergedColumnsIds = fg('jira_list_merged_column_gate')
		? (columns.find((col) => col?.id === COLUMN_ID_MERGED_COLUMN)?.mergedColumns ?? []).map(
				(col) => col.id,
			)
		: [];

	fieldSetIds = [...mergedColumnsIds, ...fieldSetIds];
	const operationScope = getOperationScope(projectKey);

	let fieldSetsScope = null;
	if (fg('jira_nin_last_used_jql')) {
		fieldSetsScope = {
			operationScope,
			projectKey: projectKey ?? null,
		};
	} else if (isInlineFieldConfigEnabled) {
		fieldSetsScope = {
			operationScope,
			projectKey,
		};
	}

	return {
		cloudId,
		amountOfColumns,
		fieldSetsInput: {
			fieldSetIds,
		},
		fieldSetIds,
		first: pageSize,
		namespace: '',
		shouldQueryFieldSetsById: true,
		filterId: null,
		viewId: null,
		isReparentingEnabled,
		isTimelineEnabled,
		scope: fieldSetsScope,
		isInlineEditingEnabled,
	};
};

export const getChildQueryVariablesForJql = <TColumn extends Column>({
	cloudId,
	columns,
	jql,
	isReparentingEnabled,
	isTimelineEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
	isHideDoneItemsEnabled,
	isConditionalFormattingEnabled,
	projectKey,
	isInlineFieldConfigEnabled,
	isLinkRelationshipFieldEnabled,
	isRemoteLinkedIssuesEnabled,
	isEditableSyncLoaded,
	isInlineEditingEnabled,
}: ChildQueryForJqlProps<TColumn>) => {
	return {
		...getChildQueryVariablesBase({
			cloudId,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			actionColumns,
			amountOfColumns,
			pageSize,
			projectKey,
			isInlineFieldConfigEnabled,
			isInlineEditingEnabled,
		}),
		issueSearchInput: { jql },
		shouldQueryHasChildren: false,
		isConditionalFormattingEnabled,
		isLinkRelationshipFieldEnabled,
		isRemoteLinkedIssuesEnabled,
		isEditableSyncLoaded,
		...(isHideDoneItemsEnabled &&
			fg('jsc_list_hide_done_items') && {
				viewConfigInput: {
					staticViewInput: {
						isHideDoneEnabled: true,
					},
				},
			}),
	};
};

export interface ChildQueryForParentIssueProps<TColumn extends Column, TSortOrder>
	extends BaseProps<TColumn> {
	parentIssueKey: string;
	sortField?: string;
	sortDirection?: TSortOrder;
	jql?: string;
	isReparentingEnabled: boolean;
	isHideDoneItemsEnabled: boolean;
	isConditionalFormattingEnabled: boolean;
	isLinkRelationshipFieldEnabled: boolean;
	isRemoteLinkedIssuesEnabled: boolean;
	isEditableSyncLoaded: boolean;
}

export const getChildQueryVariablesForParentIssue = <TColumn extends Column, TSortOrder>({
	cloudId,
	columns,
	parentIssueKey,
	jql,
	isReparentingEnabled,
	isTimelineEnabled,
	isHideDoneItemsEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
	isConditionalFormattingEnabled,
	isInlineFieldConfigEnabled,
	isLinkRelationshipFieldEnabled,
	isRemoteLinkedIssuesEnabled,
	isEditableSyncLoaded,
	isInlineEditingEnabled,
}: ChildQueryForParentIssueProps<TColumn, TSortOrder>): ChildRowsPreloadedQuery$variables => {
	const projectKey = getProjectKeyFromIssueKey(parentIssueKey);

	return {
		...getChildQueryVariablesBase({
			cloudId,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			actionColumns,
			amountOfColumns,
			pageSize,
			projectKey,
			isInlineFieldConfigEnabled,
			isInlineEditingEnabled,
		}),
		issueSearchInput: {
			childIssuesInput: {
				parentIssueKey,
				filterByProjectKeys: [projectKey],
				...(jql
					? {
							jql,
						}
					: {}),
				...(fg('jsc_list_hide_done_items') && { namespace: 'ISSUE_NAVIGATOR' }),
			},
		},
		projectKeys: [projectKey],
		projectKey,
		viewConfigInput: {
			staticViewInput: {
				isHierarchyEnabled: true,
				isGroupingEnabled: false,
				...(fg('jsc_list_hide_done_items') && { isHideDoneEnabled: isHideDoneItemsEnabled }),
			},
		},
		shouldQueryHasChildren: true,
		isConditionalFormattingEnabled,
		isLinkRelationshipFieldEnabled,
		isRemoteLinkedIssuesEnabled,
		isEditableSyncLoaded,
	};
};
